import withClearCache from "ClearCache";
import PageNotFound from "screens/Shared/PageNotFound";
import Notifications from "utils/notifications";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { createTheme } from "theme";
import "index.css";
import PrivateRoute from "global/components/PrivateRoute/PrivateRoute";
import { useAppSelector } from "hooks/use-app-select";
import { selectAuthenticated, selectLastVisitedLink, setLastVisitedLink } from "redux/authSlice";
import Layout from "layouts/Layout";
import { Route, Router, Switch } from "react-router-dom";
import history from "utils/history";
import Login from "screens/Auth/Login";
import ForgotPassword from "screens/Auth/ForgotPassword";
import NewPassword from "screens/Auth/NewPassword";
import OTP from "screens/Auth/OTP";
import viewpaths from "global/constants/ViewPathConstants";
import ErrorBoundary from "global/components/ErrorBoundary/ErrorBoundary";
import { ScrollToTop } from "utils/ScrollToTop";
import { selectTheme } from "redux/preferencesSlice";
import SetupMFA from "screens/Auth/SetupMFA";
import AuthGuard from "global/components/PrivateRoute/AuthGuard";
import Dashboard from "screens/Dashboard/Dashboard";
import Contacts from "screens/Contacts/Contacts";
import ViewContact from "screens/Contacts/ViewContact/ViewContact";
import Groups from "screens/Groups/Groups";
import Templates from "screens/Templates/Templates";
import DesignTemplate from "screens/Templates/Components/DesignTemplate";
import Campaigns from "screens/Campaigns/Campaigns";
import Users from "screens/Settings/Users/Users";
import ViewUser from "screens/Settings/Users/ViewUser";
import MyPlan from "screens/Settings/MyPlan/MyPlan";
import MyProfile from "screens/Settings/MyProfile/MyProfile";
import Register from "screens/Auth/Register";
import RegistrationSuccess from "screens/Auth/RegistrationSuccess";
import RegularCampaign from "screens/Campaigns/CreateCampaign/RegularCampaign/RegularCampaign";
import Checkout from "screens/Settings/MyPlan/Checkout/Checkout";
import RoleManagement from "screens/Settings/RoleManagement/RoleManagement";
import Onboard from "screens/Onboard/Onboard";
import ImportContacts from "screens/Contacts/ImportContacts/ImportContacts";
import ViewCampaign from "screens/Campaigns/ViewCampaign/ViewCampaign";
import ViewGroup from "screens/Groups/ViewGroup/ViewGroup";
import Tags from "screens/Tags/Tags";
import ViewTag from "screens/Tags/ViewTag/ViewTag";
import Tickets from "screens/Tickets/Tickets";
import ViewTicket from "screens/Tickets/ViewTicket/ViewTicket";
import RecipientDomains from "screens/Reports/RecipientDomains";
import FailedStats from "screens/Reports/FailedStats";
import Integration from "screens/Integration/Integration";
import ABTestCampaign from "screens/Campaigns/CreateCampaign/ABTestCampaign/ABTestCampaign";
import SequentialCampaign from "screens/Campaigns/CreateCampaign/SequentialCampaign/SequentialCampaign";
import SocialCampaign from "screens/Campaigns/CreateCampaign/SocialCampaign/SocialCampaign";
import ResendCampaign from "screens/Campaigns/CreateCampaign/ResendCampaign/ResendCampaign";
import RetargetCampaign from "screens/Campaigns/CreateCampaign/RetargetCampaign/RetargetCampaign";
import Security from "screens/Settings/Security/Security";
import Clarity from "@microsoft/clarity";
import urls from "global/constants/UrlConstants";
import strings from "./global/constants/StringConstants";
import { useEffect } from "react";
import SendersDomains from "screens/Settings/SendersDomains/SendersDomains";
import ArchivedContacts from "screens/Contacts/ArchivedContacts/ArchivedContacts";
import { useAppDispatch } from "utils/hooks";
const App = () => {
  const themePreference = useAppSelector(selectTheme);
  const dispatch = useAppDispatch();
  const current = useAppSelector(selectLastVisitedLink);
  const isAuthenticated = useAppSelector(selectAuthenticated);

  const theme = createTheme({
    colorPreset: "mailzzy",
    contrast: "normal",
    direction: "ltr",
    paletteMode: themePreference,
    responsiveFontSizes: true,
  });

  useEffect(() => {
    if (urls.PROD) {
      Clarity.init(strings.CLARITY_PROJECT_ID);
    }
  }, []);


  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <ClearCacheComponent mode={themePreference} />
      </ErrorBoundary>
    </ThemeProvider>
  );
};

const routes = [
  {
    path: `${viewpaths.dashboardViewPath}`,
    component: Dashboard,
  },
  {
    path: `${viewpaths.contactsViewPath}`,
    component: Contacts,
  },
  {
    path: `${viewpaths.archivedContactsViewPath}`,
    component: ArchivedContacts,
  },
  {
    path: `${viewpaths.importantContactsViewPath}`,
    component: ImportContacts,
  },
  { path: `${viewpaths.viewContactViewPath}/:id`, component: ViewContact },
  {
    path: `${viewpaths.groupsViewPath}`,
    component: Groups,
  },
  { path: `${viewpaths.viewGroupsViewPath}/:id`, component: ViewGroup },
  {
    path: `${viewpaths.tagsViewPath}`,
    component: Tags,
  },
  { path: `${viewpaths.viewTagViewPath}/:id`, component: ViewTag },

  // Campaigns
  {
    path: `${viewpaths.campaignsViewPath}`,
    component: Campaigns,
  },
  {
    path: `${viewpaths.CampaignStatsViewPath}`,
    component: ViewCampaign,
  },
  {
    path: `${viewpaths.createRegularCampaignViewPath}`,
    component: RegularCampaign,
  },
  {
    path: `${viewpaths.createABTestCampaignViewPath}`,
    component: ABTestCampaign,
  },
  {
    path: `${viewpaths.createSequentialCampaignViewPath}`,
    component: SequentialCampaign,
  },
  {
    path: `${viewpaths.createResendCampaignViewPath}`,
    component: ResendCampaign,
  },
  {
    path: `${viewpaths.createRetargetCampaignViewPath}`,
    component: RetargetCampaign,
  },
  {
    path: `${viewpaths.createSocialCampaignViewPath}`,
    component: SocialCampaign,
  },
  {
    path: `${viewpaths.templatesViewPath}`,
    component: Templates,
  },
  {
    path: `${viewpaths.templateDesignViewPath}`,
    component: DesignTemplate,
    hideNavs: true,
  },
  {
    path: `${viewpaths.templateDesignViewPath}/:id`,
    component: DesignTemplate,
    hideNavs: true,
  },
  {
    path: `${viewpaths.myProfileViewPath}`,
    component: MyProfile,
  },
  {
    path: `${viewpaths.securityViewPath}`,
    component: Security,
  },
  {
    path: `${viewpaths.rolesViewPath}`,
    component: RoleManagement,
  },
  {
    path: `${viewpaths.usersViewPath}`,
    component: Users,
  },
  {
    path: `${viewpaths.domainsViewPath}`,
    component: SendersDomains,
  },
  { path: `${viewpaths.viewUserViewPath}/:id`, component: ViewUser },
  { path: `${viewpaths.viewMyPlanViewPath}`, component: MyPlan },
  { path: `${viewpaths.checkout}`, component: Checkout },
  { path: `${viewpaths.ticketsViewPath}`, component: Tickets },
  { path: `${viewpaths.viewTicketsViewPath}/:id`, component: ViewTicket },
  {
    path: `${viewpaths.reportsViewPath}`,
    component: RecipientDomains,
  },
  {
    path: `${viewpaths.recipientDomainsViewPath}`,
    component: RecipientDomains,
  },
  {
    path: `${viewpaths.failedStatsViewPath}`,
    component: FailedStats,
  },
  {
    path: `${viewpaths.integrationsViewPath}`,
    component: Integration,
  },
];

const MainApp = () => {
  const isAuthenticated = useAppSelector(selectAuthenticated);

  const protectedRoutes = routes.map((route, index) => (
    <AuthGuard
      key={index}
      exact
      isLoggedIn={isAuthenticated}
      path={route.path}
      component={route.component}
    />
  ));

  return (
    <Box>
      <Router history={history}>
        <ScrollToTop />
        <Switch>
          <Route
            exact
            path={[viewpaths.landingViewPath, viewpaths.loginViewPath]}
            component={Login}
          />
          <Route
            exact
            path={[viewpaths.registerViewPath]}
            component={Register}
          />
          <Route
            exact
            path={[
              viewpaths.registrationSuccessViewPath,
              viewpaths.verificationPendingViewPath,
            ]}
            component={RegistrationSuccess}
          />
          <Route
            exact
            path={[viewpaths.forgotPasswordViewPath]}
            component={ForgotPassword}
          />
          <Route
            exact
            path={[viewpaths.resetPasswordViewPath]}
            component={NewPassword}
          />
          <Route exact path={[viewpaths.verifyTOTPViewPath]} component={OTP} />
          <Route
            exact
            path={[viewpaths.setupMFAViewPath]}
            component={SetupMFA}
          />
          <Route
            exact
            path={[viewpaths.onBoardCheckoutViewPath]}
            component={Onboard}
          />
          <PrivateRoute
            isLoggedIn={isAuthenticated}
            path={routes.map((route) => route.path)}
            component={(props: any) => {
              const currentRoute = routes.find((route) =>
                Array.isArray(route.path)
                  ? route.path.includes(props.location.pathname)
                  : route.path === props.location.pathname
              );
              return (
                <Layout hideNavs={currentRoute?.hideNavs ?? false}>
                  <Switch>{protectedRoutes}</Switch>
                </Layout>
              );
            }}
          />
          <Route path={""} component={PageNotFound} />
        </Switch>
      </Router>
      <Notifications />
    </Box>
  );
};

const ClearCacheComponent = withClearCache(MainApp);

export default App;
